// src/components/superadmin/SuperAdminUserView.js
import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BiUserCircle, BiBuilding, BiLock, BiSave, BiX } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js"; // Ensure correct path

const SuperAdminUserView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(""); // For success/error messages

  // Password Reset State
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  // Fetch User Data Function (memoized)
  const fetchUserData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      // Use the new super-admin specific route
      const response = await axios.get(
        `${API_BASE_URL}/super-admin/users/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUserProfile(response.data);
    } catch (err) {
      setError("Failed to fetch user profile.");
      console.error("Error fetching user profile:", err);
    } finally {
      setLoading(false);
    }
  }, [id]); // Dependency on ID

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]); // Call fetchUserData

  // Handle Password Reset Submission
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setIsResettingPassword(true);
    setError(null);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      // Call the existing password reset endpoint
      await axios.put(
        `${API_BASE_URL}/users/reset-password/${id}`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage("Password reset successfully!");
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordFields(false); // Hide fields after success
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password.");
      console.error("Error resetting password:", err);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const formatDate = (dateString) =>
    dateString ? new Date(dateString).toLocaleString() : "N/A";
  const formatRoleName = (roleName = "") =>
    roleName
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  if (loading)
    return (
      <div className="text-center p-6 text-gray-500">Loading profile...</div>
    );

  return (
    <div className="container mx-auto p-4 max-w-3xl">
      {/* Back Link */}
      <Link
        to="/super-admin/users"
        className="text-blue-600 hover:underline mb-4 inline-block"
      >
        &larr; Back to All Users
      </Link>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}
      {message && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">
          {message}
        </div>
      )}

      {!userProfile && !loading ? (
        <div className="text-center p-6 text-red-500 bg-red-50 rounded-lg">
          User not found or access denied.
        </div>
      ) : (
        <div className="bg-white shadow-lg rounded-lg p-6 space-y-6">
          {/* User Header */}
          <div className="flex items-center space-x-4 pb-4 border-b">
            <BiUserCircle className="w-16 h-16 text-gray-500" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {userProfile.first_name} {userProfile.last_name}
              </h1>
              <p className="text-gray-600 capitalize">
                {formatRoleName(userProfile.role_name)}
              </p>
            </div>
          </div>

          {/* User Details */}
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-3">
              User Information
            </h2>
            <dl className="space-y-2 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <dt className="font-medium text-gray-500">Email:</dt>
                <dd className="col-span-2 text-gray-800">
                  {userProfile.email}
                </dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="font-medium text-gray-500">Phone:</dt>
                <dd className="col-span-2 text-gray-800">
                  {userProfile.phone_number || "N/A"}
                </dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="font-medium text-gray-500">Status:</dt>
                <dd className="col-span-2">
                  <span
                    className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      userProfile.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {userProfile.is_active ? "Active" : "Inactive"}
                  </span>
                </dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="font-medium text-gray-500">Created:</dt>
                <dd className="col-span-2 text-gray-800">
                  {formatDate(userProfile.created_at)}
                </dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="font-medium text-gray-500">Last Updated:</dt>
                <dd className="col-span-2 text-gray-800">
                  {formatDate(userProfile.updated_at)}
                </dd>
              </div>
            </dl>
          </div>

          {/* Organization Details (if applicable) */}
          {userProfile.organization_id && (
            <div className="pt-4 border-t">
              <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                <BiBuilding className="w-5 h-5 mr-2 text-gray-500" />{" "}
                Organization
              </h2>
              <dl className="space-y-2 text-sm">
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-gray-500">Name:</dt>
                  <dd className="col-span-2 text-gray-800">
                    <Link
                      to={`/organizations/view/${userProfile.organization_id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {userProfile.organization_name} (ID:{" "}
                      {userProfile.organization_id})
                    </Link>
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {/* Password Reset Section */}
          <div className="pt-4  border-t">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-700">
                Reset Password
              </h2>
              <button
                onClick={() => setShowPasswordFields(!showPasswordFields)}
                className="text-sm text-blue-600 hover:underline"
              >
                {showPasswordFields ? "Cancel Reset" : "Change User Password"}
              </button>
            </div>

            {showPasswordFields && (
              <form
                onSubmit={handlePasswordReset}
                className="mt-4 space-y-4 p-4 bg-gray-50 rounded-md border"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength="6"
                    className="mt-1 p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength="6"
                    className="mt-1 p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isResettingPassword}
                    className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center"
                  >
                    <BiLock className="w-4 h-4 mr-1" />{" "}
                    {isResettingPassword ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminUserView;
